/**
 * VerticalFeaturesSection — présentation produit alignée dashboard
 */

import React, { useRef, useEffect, useState, memo } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard,
  Palette,
  Truck,
  CreditCard,
  MessageSquare,
  ArrowRight,
} from "lucide-react";
import { Button, Card, CardBody } from "@/components/ui";

import dashboardImage from "@/assets/features/tableau-de-bord-des-commandes-Cashlive.png";
import paymentImage from "@/assets/features/paiement-Sécurisé-CashLive.png";
// @ts-ignore - Vite gère les espaces dans les noms de fichiers
import shippingConfigImage from "@/assets/features/FireShot Capture 024 - Configuration de l'espace de vente - CashLive - [localhost].png";
// @ts-ignore - Vite gère les espaces dans les noms de fichiers
import liveShoppingImage from "@/assets/features/FireShot Capture 028 - Cashlive - Paiement Sécurisé CashLive - [localhost].png";
import chatSavImage from "@/assets/features/chat-sav.png";

interface FeatureData {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  details: string;
  image: string;
  imagePosition: "left" | "right";
}

interface VerticalFeaturesSectionProps {
  currentUser: any;
  hasOrganizations: boolean;
  openAuthModal: (registerMode: boolean) => void;
  t: (key: string) => string;
}

const getFeaturesData = (t: (key: string) => string): FeatureData[] => [
  {
    id: "dashboard",
    icon: LayoutDashboard,
    title: t("landing.verticalFeatures.dashboard.title"),
    description: t("landing.verticalFeatures.dashboard.description"),
    details: t("landing.verticalFeatures.dashboard.details"),
    image: dashboardImage,
    imagePosition: "right",
  },
  {
    id: "customization",
    icon: Palette,
    title: t("landing.verticalFeatures.customization.title"),
    description: t("landing.verticalFeatures.customization.description"),
    details: t("landing.verticalFeatures.customization.details"),
    image: paymentImage,
    imagePosition: "left",
  },
  {
    id: "shipping",
    icon: Truck,
    title: t("landing.verticalFeatures.shipping.title"),
    description: t("landing.verticalFeatures.shipping.description"),
    details: t("landing.verticalFeatures.shipping.details"),
    image: shippingConfigImage,
    imagePosition: "right",
  },
  {
    id: "payments",
    icon: CreditCard,
    title: t("landing.verticalFeatures.payments.title"),
    description: t("landing.verticalFeatures.payments.description"),
    details: t("landing.verticalFeatures.payments.details"),
    image: liveShoppingImage,
    imagePosition: "left",
  },
  {
    id: "chat-sav",
    icon: MessageSquare,
    title: t("landing.verticalFeatures.chatSav.title"),
    description: t("landing.verticalFeatures.chatSav.description"),
    details: t("landing.verticalFeatures.chatSav.details"),
    image: chatSavImage,
    imagePosition: "right",
  },
];

const useReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  return prefersReducedMotion;
};

const useIntersectionObserver = (threshold = 0.15, rootMargin = "-40px") => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, hasAnimated]);

  return { ref, isVisible };
};

const FeatureItem = memo<{
  feature: FeatureData;
  reducedMotion: boolean;
}>(({ feature, reducedMotion }) => {
  const { ref, isVisible } = useIntersectionObserver();
  const IconComponent = feature.icon;
  const isLeft = feature.imagePosition === "left";
  const show = isVisible || reducedMotion;

  return (
    <div
      ref={ref}
      className="py-12 sm:py-16 lg:py-20"
      style={{
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(16px)",
        transition: reducedMotion ? "none" : "opacity 0.4s ease, transform 0.4s ease",
      }}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
            isLeft ? "" : "lg:grid-flow-dense"
          }`}
        >
          <div className={isLeft ? "lg:order-1" : "lg:order-2"}>
            <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-md">
              <img
                src={feature.image}
                alt={feature.title}
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
              />
            </div>
          </div>

          <div className={`${isLeft ? "lg:order-2" : "lg:order-1"} space-y-4`}>
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-10">
              <IconComponent className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground tracking-tight">
              {feature.title}
            </h3>
            <p className="text-base sm:text-lg text-foreground-secondary leading-relaxed">
              {feature.description}
            </p>
            <Card variant="outlined">
              <CardBody className="p-4 sm:p-5">
                <p className="text-sm text-foreground-secondary leading-relaxed">{feature.details}</p>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
});

FeatureItem.displayName = "FeatureItem";

const VerticalFeaturesSection: React.FC<VerticalFeaturesSectionProps> = memo(
  ({ currentUser, hasOrganizations, openAuthModal, t }) => {
    const reducedMotion = useReducedMotion();
    const features = getFeaturesData(t);
    const primaryHref = hasOrganizations ? "/organizations/manage" : "/organizations/create";

    return (
      <section id="key-features" className="relative bg-background-secondary border-y border-border">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-4 text-center">
          <p className="text-sm font-medium text-primary mb-2">
            {t("landing.verticalFeatures.header.badge")}
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground tracking-tight">
            {t("landing.verticalFeatures.header.titleStart")}{" "}
            <span className="text-primary">{t("landing.verticalFeatures.header.titleHighlight")}</span>
          </h2>
          <p className="mt-3 text-foreground-secondary leading-relaxed">
            {t("landing.verticalFeatures.header.subtitle")}
          </p>
        </div>

        <div>
          {features.map((feature) => (
            <FeatureItem key={feature.id} feature={feature} reducedMotion={reducedMotion} />
          ))}
        </div>

        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
          <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
            {t("landing.verticalFeatures.cta.title")}
          </h3>
          <p className="text-sm sm:text-base text-foreground-secondary mb-6">
            {t("landing.verticalFeatures.cta.subtitle")}
          </p>
          {currentUser ? (
            <Link to={primaryHref}>
              <Button size="lg" variant="primary" rightIcon={<ArrowRight className="h-4 w-4" />}>
                {hasOrganizations
                  ? t("landing.verticalFeatures.cta.buttonMyOrgs")
                  : t("landing.verticalFeatures.cta.buttonCreate")}
              </Button>
            </Link>
          ) : (
            <Button
              size="lg"
              variant="primary"
              onClick={() => openAuthModal(false)}
              rightIcon={<ArrowRight className="h-4 w-4" />}
            >
              {t("landing.verticalFeatures.cta.buttonCreate")}
            </Button>
          )}
        </div>
      </section>
    );
  },
);

VerticalFeaturesSection.displayName = "VerticalFeaturesSection";

export default VerticalFeaturesSection;
